// 給与ビュー（中身/TMS・スプリント01／F1「月次の支給予定額一覧」＋ 02／F2「日給の日数入力」）。
// docs/spec.md §4 F1・F2。選択月の乗務員別「支給予定額」を一覧で出す。
//   歩合 ＝ 当月（積日基準）の自社便 運賃 × 乗務員別歩合率（便ごと丸めず月合計で四捨五入）
//   月給 ＝ 乗務員マスタの月額（その月に便が0件でも出る）
//   日給 ＝ 日額 × 支給対象日数。日数は経理が入れる。既定値は**暦日ベースの稼働日**（土日祝を含む・D-2）
// 額の算出は純関数 data/payroll/monthly.core.js。ここは「読む・並べる・説明する」だけ。
// 歩合の額は収支（運行実績×収支の乗務員別タブ）の人件費と ±0 で一致する。一致は
// 「同じ便・同じ率解決（operations.js の laborRateFor）・同じ端数処理」を通すことで成立させており、
// どちらかの結果を写して合わせてはいない（test_payroll_monthly.mjs が突き合わせで固定）。
// 参考表示の「稼働日（営業日・収支）」も同じ考え方で、収支エンジン computeOperations の出力
// （drivers[].days）をそのまま並べる＝営業日ユニオンの式をここへコピーしない（J-2）。
import { LABOR_RATE, getLabor, computeOperations } from '../data/analytics/operations.js'
import { computePayrollMonth, payrollMonths, tripMonth } from '../data/payroll/monthly.core.js'
import { loadPayrollDays, savePayrollDay } from '../data/payroll/payrollDays.js'
import { normDays, normDaysInput, applyDayEdit, MAX_DAYS } from '../data/payroll/workdays.core.js'
import { COLLECTIONS, listDocs } from '../data/collections.js'
import { refreshLabor } from '../data/laborRates.js'
import { supabaseReady } from '../supabase.js'
import { resolveMonth, setSelectedMonth } from '../data/selectedMonth.js'
import sampleOps from './sample.ops.json'
import { allowSample, DEMO_TAG, noDataHtml } from '../data/demoData.js'

const YEN = (n) => '¥' + Math.round(Number(n) || 0).toLocaleString('ja-JP')
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const PCT = (p) => (Math.round(Number(p) * 100) / 100) + '%'

const WAGE_LABEL = { commission: '歩合', monthly: '月給', daily: '日給' }
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` }

const pad2 = (n) => String(n).padStart(2, '0')
// デジタコの月（収支ビュー operations.js の monthOf と同じ解決。sd は [月,日] で年を持たない）
const tachoMonth = (t) => t.month || (t.sd ? '2026-' + pad2(t.sd[0]) : (t.date ? String(t.date).slice(0, 7) : ''))

const P = { el: null, ctx: {}, month: null, months: [], trips: [], tachos: [], drivers: null, estimateRate: null,
  online: false, fetchError: false, isSample: false, sort: { key: 'code', dir: 'asc' },
  days: {}, // 支給対象日数 { 'YYYY-MM': { code: 日数 } }。全月ぶんを保持＝月を往復しても消えない
  // 書き込みは applyDayEdit（workdays.core.js）だけを通す。書き先の月は入力欄の data-month で決まる（02 eval B1）
  opsCache: { month: null, map: null }, // 収支の稼働日（営業日ユニオン）を月ごとにキャッシュ
  saveTimers: new Map(), // デバウンス保存のタイマー。キーは '月|コード'＝別の月・別の乗務員の保存を取り消さない
  saveError: false, out: null }

// 収支に出ている「稼働日」（営業日ユニオン）を収支エンジンからそのまま取る。
// 給与側で数え直さない＝画面に並ぶ 2 つの日数が同じ元データから出ていることを保証する。
function opsDaysFor(month) {
  if (P.opsCache.month === month) return P.opsCache.map
  let map = null
  try {
    const trips = P.trips.filter((t) => tripMonth(t) === month)
    const tachos = P.tachos.filter((t) => tachoMonth(t) === month)
    const ops = computeOperations(trips, tachos, { drivers: P.drivers, labor: P.estimateRate })
    map = {}
    for (const d of ops.drivers) map[d.code] = d.days
  } catch { map = null } // 収支が出せない場面では「—」。数字を捏造しない
  P.opsCache = { month, map }
  return map
}

function styleOnce() {
  if (document.getElementById('py-style')) return
  const s = document.createElement('style'); s.id = 'py-style'
  s.textContent = `
  .py{display:flex;flex-direction:column;gap:12px}
  .py-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .py-bar select{font-family:inherit;font-size:13px;padding:6px 10px;border:1px solid var(--e1);border-radius:8px;background:var(--card);color:var(--text)}
  .py-open{font-size:12px;color:var(--text);text-decoration:none;background:var(--card);border:1px solid var(--e1);border-radius:8px;padding:6px 12px}
  .py-open.right{margin-left:auto}
  .py-demo{font-size:11px;color:var(--signal);background:transparent;border:1px solid var(--accent);border-radius:999px;padding:3px 10px}
  .py-kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:10px}
  .py-kpi{background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:11px 13px}
  .py-kpi .k{font-size:10.5px;color:var(--muted)}
  .py-kpi .v{font-size:19px;font-weight:700;font-variant-numeric:tabular-nums;margin-top:2px}
  .py-kpi .s{font-size:10px;color:var(--muted);margin-top:1px}
  .py-tblwrap{border:1px solid var(--e1);border-radius:12px;overflow:hidden;background:var(--card)}
  .py-tblwrap .cap{padding:9px 13px;font-size:12px;font-weight:700;color:var(--muted);background:var(--head);border-bottom:1px solid var(--e1)}
  .py-scroll{max-height:56vh;overflow:auto}
  table.py-tbl{width:100%;border-collapse:collapse;font-size:12px}
  .py-tbl th,.py-tbl td{padding:7px 11px;border-bottom:1px solid var(--e3);text-align:left;white-space:nowrap}
  .py-tbl th{position:sticky;top:0;background:var(--head);color:var(--text);font-size:10.5px;z-index:1}
  .py-tbl th.sortable{cursor:pointer;user-select:none}
  .py-tbl th.sortable:hover{background:var(--e2);color:var(--signal)}
  .py-tbl th .arw{color:var(--text);font-size:9px;margin-left:2px}
  .py-tbl td.n,.py-tbl th.n{text-align:right;font-variant-numeric:tabular-nums}
  .py-tbl td.amt{font-weight:700}
  .py-tbl td.pend{color:var(--muted)}
  .py-tbl tfoot td{background:var(--head);font-weight:800;border-top:1px solid var(--e1);border-bottom:0}
  .py-kind{font-size:10.5px;font-weight:700;border:1px solid var(--e1);border-radius:999px;padding:1px 8px;color:var(--text);background:var(--head)}
  .py-basis{color:var(--text)}
  .py-warn{display:inline-block;font-size:10.5px;color:var(--signal);border:1px solid var(--accent);border-radius:999px;padding:1px 7px;margin-left:6px}
  .py-off{font-size:10.5px;color:var(--muted);margin-left:6px}
  .py-notice{font-size:12px;color:var(--text);background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:9px 13px}
  .py-note{font-size:11px;color:var(--muted);line-height:1.6}
  .py-days{width:56px;font-family:inherit;font-size:12px;text-align:right;padding:3px 6px;border:1px solid var(--e1);border-radius:6px;background:var(--card);color:var(--text);font-variant-numeric:tabular-nums}
  .py-days:focus{outline:none;border-color:var(--accent)}
  .py-adopt{font-size:10.5px;font-family:inherit;color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:1px 9px;margin-left:6px;cursor:pointer}
  .py-adopt:hover{color:var(--signal);border-color:var(--accent)}
  .py-refs{display:flex;gap:5px;flex-wrap:wrap;margin-top:4px}
  .py-chip{font-size:10px;font-family:inherit;color:var(--muted);background:transparent;border:1px solid var(--e1);border-radius:999px;padding:1px 8px;cursor:pointer}
  .py-chip:hover{color:var(--signal);border-color:var(--accent)}
  .py-chip.off{cursor:default;opacity:.6}
  .py-chip.cal{color:var(--text)}
  .py-diff{font-size:10px;color:var(--signal);border:1px solid var(--accent);border-radius:999px;padding:1px 8px}
  `
  document.head.appendChild(s)
}

export async function renderPayroll(el, ctx = {}) {
  styleOnce()
  P.el = el
  P.ctx = ctx
  P.online = !ctx.demo && supabaseReady
  P.fetchError = false
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">集計しています…</b><span>選択月の支給予定額を計算しています</span></div>'
  // 乗務員マスタ・試算用率は収支エンジンと同じ設定ローダーから取る（＝収支と同じ率で計算する）。
  // 描画のたびに取り直すので、乗務員画面で率・月額を直した直後でも再読込なしで反映される。
  try { await refreshLabor(true) } catch { /* 既定（試算用率35%）のまま続行 */ }
  const cfg = getLabor()
  P.drivers = cfg.drivers || null
  P.estimateRate = cfg.rate != null ? cfg.rate : LABOR_RATE

  let trips = sampleOps.trips, tachos = sampleOps.tachographs
  if (P.online) {
    try {
      const [tr, ta] = await Promise.all([listDocs(COLLECTIONS.trips), listDocs(COLLECTIONS.tachographs)])
      if (tr && tr.length) { trips = tr; tachos = ta || [] }
      else if (!allowSample(P.online)) { trips = []; tachos = [] } // 実データ0件。サンプルへ落ちてよいかは共通判定に委ねる
    } catch { P.online = false; P.fetchError = true }
  }
  // サインイン済みの実fetch失敗はサンプルへフォールバックしない（架空の給与額を本物の顔で出さない）
  if (P.fetchError) { P.trips = []; P.tachos = []; P.isSample = false; paintFetchError(); return }
  P.isSample = trips === sampleOps.trips
  P.trips = trips; P.tachos = tachos
  P.opsCache = { month: null, map: null }
  // 支給対象日数（月・乗務員ごと）。全月ぶんを一度に読む＝月を往復しても取り直さない（D-13）
  try { P.days = await loadPayrollDays(P.online) || {} } catch { P.days = {}; P.saveError = true }
  const picked = resolveMonth(payrollMonths(trips))
  // 便のある月はすべて選べる（D-13）。加えて「今月」は便が未取込でも選べるようにする
  // （固定給の乗務員は便が無くても支給がある＝月初に開いて確認できる必要がある）。
  // 既定の表示月は resolveMonth のまま＝他画面と同じ月。ここで今月へ勝手に飛ばさない。
  P.months = [...new Set([...picked.months, thisMonth()])].sort()
  P.month = picked.month
  if (!P.month) { paintNoData(); return } // 便も共有月も無い＝月を決められない
  paint()
}

// 実fetch失敗（デモ意図とは別）の主表示。サンプルの給与額は描かない
function paintFetchError() {
  P.el.innerHTML = '<section class="py"><div class="ct-state is-error"><b class="ct-state__t">給与を計算できませんでした</b><span>保存済みの運行実績・乗務員マスタを取得できませんでした。誤った支給額を出さないため、サンプルは表示していません。接続を確認して再試行してください</span><button type="button" class="btn" id="py-retry">再試行</button></div></section>'
  const retry = P.el.querySelector('#py-retry'); if (retry) retry.addEventListener('click', () => { renderPayroll(P.el, P.ctx) })
}

// 実データ0件＋デモデータ表示OFF（既定）の主表示
function paintNoData() {
  P.el.innerHTML = `<section class="py">${noDataHtml('給与')}</section>`
}

// 計算根拠の要約（1行で「なぜその額か」が読める形）
function basisOf(r) {
  if (r.wageType === 'commission') {
    const rate = r.ratePctUsed != null ? PCT(r.ratePctUsed) : r.ratePcts.map(PCT).join('／')
    const hist = r.ratePctUsed == null && r.ratePcts.length > 1 ? '（月中に改定）' : ''
    return `運賃 ${YEN(r.fare)} × ${rate}${hist} <span class="py-off">自社便 ${r.tripCount}便</span>`
  }
  if (r.wageType === 'monthly') {
    return r.monthlySalary != null
      ? `月額 ${YEN(r.monthlySalary)}${r.tripCount ? ` <span class="py-off">当月の自社便 ${r.tripCount}便（額には影響しません）</span>` : ''}`
      : '月額 —'
  }
  return daysCell(r)
}

// 日給の行：日額 × 【日数入力欄】＋ 参考表示（暦日・営業日・デジタコ）。
// 既定値は暦日ベース（土日祝を含む・D-2）だが、確定操作（Enter／採用／参考値クリック）まで額は出さない。
function daysCell(r) {
  const wage = r.dailyWage != null ? YEN(r.dailyWage) : '—'
  const val = r.days == null ? '' : String(r.days)
  const cd = esc(r.code)
  const chips = [
    ['稼働日（暦日）', r.calDays, ' cal', '積日→卸日の日ユニオン。土日祝も 1 日として数えます（既定値）'],
    ['稼働日（営業日・収支）', r.bizDays, '', '運行実績×収支の乗務員別タブに出ている稼働日（土日祝を除外）'],
    ['運行日（デジタコ）', r.tachoDays, '', 'デジタコの運行開始日のユニーク数'],
  ].map(([lb, v, cls, tip]) => v == null
    ? `<span class="py-chip off" title="${esc(tip)}">${lb}: —</span>`
    : `<button type="button" class="py-chip${cls}" data-fill="${v}" data-code="${cd}" title="${esc(tip)}／押すと日数欄に入ります">${lb}: ${v}日</button>`).join('')
  const diff = (r.bizDays != null && r.weekendDays > 0)
    ? `<span class="py-diff" title="収支の稼働日には入らない日（暦日ベースだから数える日）">土日祝に走った日 +${r.weekendDays}日</span>` : ''
  const def = `<span data-defbox="${cd}"${r.days == null ? '' : ' style="display:none"'}>`
    + `<button type="button" class="py-adopt" data-fill="${r.defaultDays}" data-code="${cd}">採用</button>`
    + `<span class="py-off">既定値（未確定）: ${r.defaultDays} 日（暦日ベース）</span></span>`
  // data-month＝この入力欄が「どの月のものか」。保存も再計算もこの値を使う（表示中の月を見ない・B1）。
  // data-clamp＝0〜31 の外を打たれたときに「丸めた」ことを出す枠（黙って直さない・B2）。
  return `日額 ${wage} × <input class="py-days" type="number" min="0" max="${MAX_DAYS}" step="1" inputmode="numeric"`
    + ` data-code="${cd}" data-month="${P.month}" value="${val}" placeholder="${r.defaultDays}" aria-label="${esc(r.name)} の支給対象日数（0〜${MAX_DAYS}）"> 日`
    + `<span class="py-warn" data-clamp="${cd}" style="display:none"></span>`
    + def
    + `<div class="py-refs">${chips}${diff}</div>`
}

function sortRows(rows) {
  const { key, dir } = P.sort, mul = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    if (key === 'amount') return ((a.amount == null ? -1 : a.amount) - (b.amount == null ? -1 : b.amount)) * mul
    if (key === 'code') {
      const na = Number(a.code), nb = Number(b.code)
      if (!isNaN(na) && !isNaN(nb)) return (na - nb) * mul
    }
    return String(a[key] == null ? '' : a[key]).localeCompare(String(b[key] == null ? '' : b[key]), 'ja') * mul
  })
}

// 選択月の集計。日数（入力値）と収支の稼働日（参考）を渡す。
function compute() {
  P.out = computePayrollMonth({
    trips: P.trips, tachos: P.tachos, month: P.month, drivers: P.drivers, estimateRate: P.estimateRate,
    dayInputs: P.days[P.month] || null, opsDays: opsDaysFor(P.month),
  })
  return P.out
}

// 日数の合計注記（未入力は合計に入っていないことを合計行の脇に出す）
const pendNote = (out, n) => out.dailyPendingCount
  ? `<span class="py-off">日給の日数 未入力 ${out.dailyPendingCount}名 は月次合計に含めていません</span>`
  : `<span class="py-off">${n}名分</span>`

function paint() {
  const el = P.el
  const out = compute()
  const rows = sortRows(out.rows)
  const opts = P.months.map((m) => `<option value="${m}" ${m === P.month ? 'selected' : ''}>${m.replace('-', '年')}月</option>`).join('')
  const COLS = [
    { key: 'code', label: 'コード' },
    { key: 'name', label: '乗務員' },
    { key: 'wageType', label: '給与区分' },
    { key: 'basis', label: '計算根拠', sortable: false },
    { key: 'amount', label: '支給予定額', num: 1 },
  ]
  const st = P.sort
  const ths = COLS.map((c) => {
    if (c.sortable === false) return `<th>${c.label}</th>`
    const arw = st.key === c.key ? `<span class="arw">${st.dir === 'asc' ? '▲' : '▼'}</span>` : ''
    return `<th class="sortable${c.num ? ' n' : ''}" data-key="${c.key}">${c.label}${arw}</th>`
  }).join('')
  const body = rows.length ? rows.map((r) => {
    // 日給の「日数未入力」だけは入力のたびに出し入れするので、行を作り直さずに切り替えられるよう分けて描く
    const warn = r.warn.filter((w) => !(r.wageType === 'daily' && w === '日数未入力'))
      .map((w) => `<span class="py-warn">⚠ ${esc(w)}</span>`).join('')
    const dwarn = r.wageType === 'daily'
      ? `<span class="py-warn" data-warndays="${esc(r.code)}"${r.needsDays ? '' : ' style="display:none"'}>⚠ 日数未入力</span>` : ''
    const amt = `<td class="n amt${r.amount == null ? ' pend' : ''}" data-amt="${esc(r.code)}">${r.amount == null ? '—' : YEN(r.amount)}</td>`
    return `<tr>
      <td>${esc(r.code) || '—'}</td>
      <td>${esc(r.name)}${r.status === 'inactive' ? '<span class="py-off">退職</span>' : ''}</td>
      <td><span class="py-kind">${WAGE_LABEL[r.wageType]}</span></td>
      <td class="py-basis">${basisOf(r)}${warn}${dwarn}</td>
      ${amt}
    </tr>`
  }).join('')
    : `<tr><td colspan="5" style="border:0"><div class="ct-state is-empty"><b class="ct-state__t">この月に支給対象の乗務員がいません</b><span>月を切り替えるか、運行実績を取り込んでください。固定給の乗務員は「乗務員」画面で給与区分を設定すると、便が無い月でも並びます</span></div></td></tr>`
  const foot = rows.length ? `<tfoot><tr>
      <td colspan="3">月次合計</td>
      <td class="py-basis" id="py-footnote">${pendNote(out, rows.length)}</td>
      <td class="n" id="py-total">${YEN(out.total)}</td>
    </tr></tfoot>` : ''

  el.innerHTML = `
    <section class="py">
      <div class="py-bar">
        <select id="py-month">${opts}</select>
        ${P.isSample ? `<span class="py-demo">${DEMO_TAG}</span>` : ''}
        <a class="py-open right" href="#/drivers">乗務員マスタ（歩合率・固定給）→</a>
        <a class="py-open" href="#/operations">運行実績×収支で突き合わせ →</a>
      </div>
      ${out.noTrips ? '<div class="py-notice">この月には取込済みの便がありません。固定給（月給・日給）の乗務員だけを表示しています。</div>' : ''}
      <div class="py-kpis">
        <div class="py-kpi"><div class="k">支給予定額 合計（当月）</div><div class="v" id="py-kpi-total">${YEN(out.total)}</div><div class="s">${P.month.replace('-', '年')}月分・総支給（手当控除は 07 で追加）</div></div>
        <div class="py-kpi"><div class="k">対象乗務員</div><div class="v">${out.rows.length}<span style="font-size:12px">名</span></div><div class="s">歩合 ${out.counts.commission} / 月給 ${out.counts.monthly} / 日給 ${out.counts.daily}</div></div>
        <div class="py-kpi"><div class="k">当月の自社便</div><div class="v">${out.tripCount}<span style="font-size:12px">便</span></div><div class="s">積日で当月に入る自社便（傭車は対象外）</div></div>
        <div class="py-kpi"><div class="k">未確定</div><div class="v" id="py-kpi-pending">${out.pendingCount}<span style="font-size:12px">名</span></div><div class="s" id="py-kpi-pendnote">${out.pendingCount ? `日給の日数未入力 ${out.dailyPendingCount}名・月額未設定など` : '額が出ていない乗務員はいません'}</div></div>
        <div class="py-kpi"><div class="k">率未設定の歩合</div><div class="v">${out.unsetRateCount}<span style="font-size:12px">名</span></div><div class="s">試算用率 ${out.estimatePct}% で計算</div></div>
      </div>
      <div class="py-tblwrap">
        <div class="cap">支給予定額 一覧（${rows.length}名）<span style="font-weight:400;color:var(--text)"> · 見出しクリックで並び替え</span></div>
        <div class="py-scroll"><table class="py-tbl">
          <thead><tr>${ths}</tr></thead>
          <tbody>${body}</tbody>
          ${foot}
        </table></div>
      </div>
      <div class="py-note">
        <b>歩合</b>＝当月（<b>積日</b>基準）の<b>自社便の運賃</b>×乗務員別歩合率（適用開始日つき）。<b>便ごとに丸めず、月合計で四捨五入</b>します。傭車便・通行料は対象に含めません。
        そのため歩合乗務員の支給予定額は、同じ月の<b>「運行実績×収支」乗務員別タブの人件費と 1 円の差もなく一致</b>します（同じ便・同じ率・同じ端数処理を通しているため）。
        <b>月給</b>＝乗務員マスタの月額（当月の便が 0 件でも出ます）。
        <b>日給</b>＝日額 × 支給対象日数。日数の既定値は<b>暦日ベースの稼働日</b>（積日→卸日の日ユニオン。<b>土日祝も 1 日として数えます</b>）で、
        参考として<b>収支の稼働日（営業日・土日祝を除外）</b>とデジタコの運行日も並べています。土日祝に走った乗務員は暦日のほうが多くなります。
        日数は<b>月・乗務員ごとに保存</b>され、過去の月にも入れられます。<b>収支画面の「稼働日」は 1 日も変わりません</b>（給与用の暦日は別の関数で数えています）。
        歩合率が未設定の乗務員は<b>試算用率 ${out.estimatePct}%</b> で計算し、行に警告を出します（正しい額にするには「乗務員」画面で歩合率を入れてください）。
        乗務員画面の<b>「人件費設定」（受注試算の時給・係数）は給与額に一切影響しません</b>。手当・控除・締め・明細印刷・CSV 出力は後続スプリントで追加します。
      </div>
    </section>`

  const sel = el.querySelector('#py-month')
  if (sel) sel.addEventListener('change', (e) => { P.month = setSelectedMonth(e.target.value) || e.target.value; paint() })
  el.querySelectorAll('th.sortable').forEach((th) => th.addEventListener('click', () => {
    const key = th.dataset.key
    if (P.sort.key === key) P.sort.dir = P.sort.dir === 'asc' ? 'desc' : 'asc'
    else P.sort = { key, dir: key === 'amount' ? 'desc' : 'asc' }
    paint()
  }))
  wireDays(el)
}

// ---- 日給の日数入力（F2）------------------------------------------------
// 入力中は行を作り直さない（フォーカスが飛ぶ）。額・合計・注記だけを差し替える。
function wireDays(el) {
  // 入力欄 1 つ分の反映。月は**その入力欄に焼いてある data-month**から取る（表示中の月ではない・B1）。
  // 0〜31 の外なら入力欄の表示も丸め後の値へ書き戻す＝「画面の日数 × 日額 ＝ 画面の額」を崩さない（B2）。
  function applyInput(inp, commit) {
    const cd = inp.dataset.code
    const res = setDays(inp.dataset.month, cd, inp.value, commit)
    if (res.clamped) {
      inp.value = res.days == null ? '' : String(res.days)
      setClampNote(cd, `⚠ 0〜${MAX_DAYS} 日で入力してください（${res.days} 日に直しました）`)
    } else if (!commit) setClampNote(cd, '') // 打ち直したら注記を消す（Tab で外れただけでは消さない）
    return res
  }
  el.querySelectorAll('input.py-days').forEach((inp) => {
    inp.addEventListener('input', () => { applyInput(inp, false) })
    inp.addEventListener('change', () => { applyInput(inp, true) })
    inp.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return
      e.preventDefault()
      // 空のまま Enter＝既定値（暦日ベース）を採用して確定する
      if (inp.value === '') { const r = rowFor(inp.dataset.code); if (r) inp.value = String(r.defaultDays) }
      applyInput(inp, true)
      inp.blur()
    })
  })
  el.querySelectorAll('[data-fill]').forEach((b) => b.addEventListener('click', () => {
    const inp = el.querySelector(`input.py-days[data-code="${cssq(b.dataset.code)}"]`)
    if (!inp) return
    inp.value = String(normDays(b.dataset.fill) ?? '')
    setClampNote(b.dataset.code, '') // 参考値は必ず範囲内。前の丸め注記を残さない
    applyInput(inp, true)
  }))
}

// 「0〜31 に丸めました」を行の上に出す／消す。text が空なら隠す。
function setClampNote(code, text) {
  const n = P.el && P.el.querySelector(`[data-clamp="${cssq(code)}"]`)
  if (!n) return
  n.textContent = text
  n.style.display = text ? '' : 'none'
}

const cssq = (v) => String(v == null ? '' : v).replace(/["\\]/g, '\\$&')
const rowFor = (code) => (P.out && P.out.rows.find((r) => r.code === code)) || null

// 日数を反映する。commit=true で保存（Enter・採用・参考値クリック・入力欄を離れたとき）。
// 入力の途中（1文字目）でも額は動く＝「即時更新」。保存はデバウンスでも走らせる。
//
// month は**その入力欄が属する月**を必ず受け取る。ここで P.month（表示中の月）を読んではいけない:
// 入力欄に打った直後に月を切り替えると change もデバウンス保存も**切替後に**発火するため、
// 表示中の月へ書くと切替先の月の保存値を黙って壊す（02 eval B1）。
function setDays(month, code, value, commit) {
  const res = applyDayEdit(P.days, month, code, value)
  if (!res.applied) return res
  const key = res.month + '|' + res.code
  const t = P.saveTimers.get(key)
  if (t) clearTimeout(t)
  P.saveTimers.delete(key)
  liveRefresh(res.month)
  if (commit) persist(res.month, res.code, res.days)
  else P.saveTimers.set(key, setTimeout(() => { P.saveTimers.delete(key); persist(res.month, res.code, res.days) }, 600))
  return res
}

function persist(month, code, d) {
  Promise.resolve(savePayrollDay(P.online, month, code, d)).catch(() => {
    P.saveError = true
    const box = P.el && P.el.querySelector('#py-footnote')
    if (box) box.innerHTML = '<span class="py-warn">⚠ 日数を保存できませんでした（接続を確認してください）</span>'
  })
}

// 額・合計・KPI・注記だけを描き替える（入力欄のフォーカスを保つ）。
// month＝編集された月。表示中でない月の編集では画面に触らない（画面と保存値を食い違わせない・B1）。
function liveRefresh(month) {
  const el = P.el
  if (!el || month !== P.month) return
  const out = compute()
  el.querySelectorAll('td[data-amt]').forEach((td) => {
    const r = rowFor(td.dataset.amt)
    if (!r) return
    td.textContent = r.amount == null ? '—' : YEN(r.amount)
    td.classList.toggle('pend', r.amount == null)
  })
  el.querySelectorAll('[data-warndays]').forEach((sp) => {
    const r = rowFor(sp.dataset.warndays)
    sp.style.display = (r && r.needsDays) ? '' : 'none'
  })
  el.querySelectorAll('[data-defbox]').forEach((sp) => {
    const r = rowFor(sp.dataset.defbox)
    sp.style.display = (r && r.days == null) ? '' : 'none'
  })
  const set = (sel, html) => { const n = el.querySelector(sel); if (n) n.innerHTML = html }
  set('#py-total', YEN(out.total))
  set('#py-kpi-total', YEN(out.total))
  set('#py-kpi-pending', `${out.pendingCount}<span style="font-size:12px">名</span>`)
  set('#py-kpi-pendnote', out.pendingCount ? `日給の日数未入力 ${out.dailyPendingCount}名・月額未設定など` : '額が出ていない乗務員はいません')
  if (!P.saveError) set('#py-footnote', pendNote(out, out.rows.length))
}
